import React, { useState, useEffect } from 'react';
import { Building2, Handshake, ArrowLeftRight, X } from 'lucide-react';

/* ─── Sub-components ────────────────────────────────────────────────── */

function SelectionCard({ icon: Icon, title, description, value, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`w-full rounded-[16px] p-5 text-left transition-all ${
        selected
          ? 'bg-[hsl(var(--succeedo-orange))]/5 border-2 border-[hsl(var(--succeedo-orange))]'
          : 'bg-white border border-[#e5e5e5] hover:border-[#a3a3a3]'
      }`}
    >
      <div className="flex items-center gap-4">
        {Icon && (
          <Icon
            className={`w-6 h-6 flex-shrink-0 ${
              selected ? 'text-[hsl(var(--succeedo-orange))]' : 'text-[#0a0a0a]'
            }`}
            strokeWidth={2}
          />
        )}
        <div className="flex-1">
          <div className="text-[16px] font-medium text-[#0a0a0a]">{title}</div>
          {description && selected && (
            <div className="text-[14px] text-[#737373] mt-1">{description}</div>
          )}
        </div>
      </div>
    </button>
  );
}

function PillButton({ label, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] transition-all ${
        selected
          ? 'bg-[#0a0a0a] text-white'
          : 'border border-[#e5e5e5] text-[#737373] hover:border-[#a3a3a3]'
      }`}
    >
      {label}
    </button>
  );
}

function IndustryPill({ label, selected, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-[13px] transition-all ${
        selected
          ? 'bg-[hsl(var(--succeedo-orange))]/10 border border-[hsl(var(--succeedo-orange))] text-[hsl(var(--succeedo-orange))]'
          : 'border border-[#e5e5e5] text-[#737373] hover:border-[#a3a3a3]'
      }`}
    >
      {label}
    </button>
  );
}

function ProgressBar({ currentStep, totalSteps }) {
  return (
    <div className="mb-8">
      <div className="font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
        STEP {currentStep} OF {totalSteps}
      </div>
      <div className="flex gap-2">
        {Array.from({ length: totalSteps }, (_, i) => (
          <div
            key={i}
            className={`flex-1 h-1 rounded-full ${
              i < currentStep ? 'bg-[hsl(var(--succeedo-orange))]' : 'bg-[#e5e5e5]'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Constants ─────────────────────────────────────────────────────── */

const PERSONA_OPTIONS = [
  {
    value: 'seller',
    icon: Building2,
    title: 'Business Owner looking to sell or transition',
  },
  {
    value: 'buyer',
    icon: Handshake,
    title: 'Looking to buy a business',
  },
  {
    value: 'both',
    icon: ArrowLeftRight,
    title: "Both \u2014 I own a business and I\u2019m also looking to acquire",
  },
];

// Seller goals carry the time horizon in the label \u2014 the previous
// step had a separate "What's your time horizon?" pill row, but the
// answer space overlapped with these labels and created contradictions
// (e.g. "Build value (3-5 years)" + "Immediate"). Collapsed into one
// question so the seller answers once.
const SELLER_GOALS = [
  'Exit within the next 12 months',
  'Exit in 1\u20133 years',
  'Build value before I sell (3\u20135 years)',
  "Explore my options \u2014 I\u2019m not sure yet",
];

const BUYER_GOALS = [
  'Find and close a deal within 6 months',
  'Find and close a deal within 12 months',
  'Building my criteria and doing research',
  'Exploring acquisition as a growth strategy',
];

// Derived time-horizon bucket per goal label. Stored on the User so
// any code that reads users.time_horizon keeps working \u2014 see
// save_succeedo_onboarding.rb. Returns nil for the "exploring" labels
// since horizon doesn't apply to undecided sellers/buyers.
const GOAL_TO_TIME_HORIZON = {
  'Exit within the next 12 months':            'immediate',
  'Exit in 1\u20133 years':                   '12-plus',
  'Build value before I sell (3\u20135 years)': '12-plus',
  "Explore my options \u2014 I\u2019m not sure yet": null,
  'Find and close a deal within 6 months':    '6-months',
  'Find and close a deal within 12 months':   '12-plus',
  'Building my criteria and doing research':   null,
  'Exploring acquisition as a growth strategy': null,
};

const ROLE_OPTIONS = [
  'Business Owner',
  'CEO / Founder',
  'CFO / Finance',
  'Broker',
  'Advisor / Consultant',
  'Investor',
  'Other',
];

const SELLER_ROLE_OPTIONS = [
  { value: 'owner', title: 'Owner' },
  { value: 'broker', title: 'Broker' },
  { value: 'advisor', title: 'Advisor' },
];

// Must match ValuationStudioWizard INDUSTRIES exactly — these map to NAICS sector clusters
const INDUSTRIES = [
  'Manufacturing',
  'Retail',
  'Technology',
  'Healthcare',
  'Professional Services',
  'Construction',
  'Hospitality',
  'Food & Beverage',
  'Transportation',
  'Real Estate',
  'Financial Services',
  'Other Services',
];

const SIZE_BANDS = [
  'Under $500K',
  '$500K\u2013$1M',
  '$1M\u2013$5M',
  '$5M\u2013$10M',
  '$10M+',
];

const EXIT_TYPES = [
  { value: 'full-exit', title: 'Full exit' },
  { value: 'majority-sale', title: 'Majority sale' },
  { value: 'minority-sale', title: 'Minority sale' },
  { value: 'family-succession', title: 'Family succession' },
  { value: 'raise-capital', title: 'Raise capital' },
];

const BUYER_TYPE_OPTIONS = [
  { value: 'individual', title: 'Individual buyer' },
  { value: 'search-fund', title: 'Search fund' },
  { value: 'operator', title: 'Operator / acquirer' },
  { value: 'pe', title: 'PE / institutional' },
  { value: 'strategic', title: 'Strategic buyer' },
  { value: 'advisor', title: 'Advisor / broker' },
];

const GEO_OPTIONS = [
  'Canada-wide',
  'Ontario',
  'Quebec',
  'BC',
  'Alberta',
  'Atlantic',
  'Prairies',
  'Territories',
];

const DEAL_ROLE_OPTIONS = ['Lead buyer', 'Investor', 'Advisor', 'Broker'];

/* ─── Wizard Component ─────────────────────────────────────────────── */

const PersonaWizard = ({ currentUser, onSave, loading: savingExternal }) => {
  // ── State ────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // Step 1
  const [persona, setPersona] = useState(null);
  // Step 2
  const [goal, setGoal] = useState(null);
  const [timeHorizon, setTimeHorizon] = useState(null);
  // Step 3
  const [roleTitle, setRoleTitle] = useState('');
  const [otherRole, setOtherRole] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [geography, setGeography] = useState('');
  // Step 4a (Seller)
  const [ownerRole, setOwnerRole] = useState(null);
  const [industrySector, setIndustrySector] = useState(null);
  const [industryDescription, setIndustryDescription] = useState('');
  const [businessSizeBand, setBusinessSizeBand] = useState(null);
  const [saleObjective, setSaleObjective] = useState(null);
  // Step 4b (Buyer)
  const [buyerType, setBuyerType] = useState(null);
  const [buyerIndustries, setBuyerIndustries] = useState([]);
  const [targetGeography, setTargetGeography] = useState(null);
  const [targetSize, setTargetSize] = useState(null);
  const [dealRole, setDealRole] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  // ── Pre-fill from query params ───────────────────────────────────
  const queryParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const requestedStep = queryParams ? parseInt(queryParams.get('step'), 10) : null;

  useEffect(() => {
    try {
      const personaParam = queryParams?.get('persona');
      if (personaParam === 'seller' || personaParam === 'buyer' || personaParam === 'both') {
        setPersona(personaParam);
      }
    } catch (_) {
      // ignore
    }
  }, []);

  // ── Pre-fill from existing user data (returning users) ──────────
  useEffect(() => {
    if (!currentUser) return;

    // If user already completed onboarding AND no specific step requested,
    // redirect to home. If ?step=N is in the URL, resume at that step instead
    // (this is how "Complete Profile" banner works).
    if (currentUser.succeedo_persona && currentUser.succeedo_goal) {
      if (requestedStep && requestedStep >= 3) {
        setStep(requestedStep);
      } else {
        window.location.href = '/succeedo/home';
        return;
      }
    }

    // Pre-fill from existing partial data
    if (currentUser.succeedo_persona) setPersona(currentUser.succeedo_persona);
    if (currentUser.succeedo_goal) setGoal(currentUser.succeedo_goal);
    if (currentUser.time_horizon) setTimeHorizon(currentUser.time_horizon);
    if (currentUser.role_title) setRoleTitle(currentUser.role_title);
    if (currentUser.succeedo_company_name) setCompanyName(currentUser.succeedo_company_name);
    if (currentUser.province) setGeography(currentUser.province);
    if (currentUser.owner_role) setOwnerRole(currentUser.owner_role);
    if (currentUser.industry_sector) setIndustrySector(currentUser.industry_sector);
    if (currentUser.industry_description) setIndustryDescription(currentUser.industry_description);
    if (currentUser.business_size_band) setBusinessSizeBand(currentUser.business_size_band);
    if (currentUser.sale_objective) setSaleObjective(currentUser.sale_objective);

    const bp = currentUser.buyer_profile;
    if (bp) {
      if (bp.buyer_type) setBuyerType(bp.buyer_type);
      if (bp.preferred_industries) setBuyerIndustries(bp.preferred_industries);
      if (bp.target_geography) setTargetGeography(bp.target_geography);
      if (bp.target_size_range) setTargetSize(bp.target_size_range);
      if (bp.deal_role) setDealRole(bp.deal_role);
    }
  }, [currentUser]);

  // ── Computed ─────────────────────────────────────────────────────

  // Total visible steps depend on persona
  const computeTotalSteps = () => {
    if (persona === 'both') return 5;
    if (persona === 'seller') return 4;
    if (persona === 'buyer') return 4;
    return 4; // default before selection
  };

  const totalSteps = computeTotalSteps();

  // Map internal step number to display step for progress bar
  // Internal steps: 1=persona, 2=goal, 3=aboutYou, 4=seller, 5=buyer
  // For seller: display 1,2,3,4
  // For buyer: display 1,2,3,4 (step 5 internally maps to display 4)
  // For both: display 1,2,3,4,5
  const displayStep = () => {
    if (persona === 'buyer' && step === 5) return 4;
    return step;
  };

  const currentGoals = persona === 'buyer' ? BUYER_GOALS : SELLER_GOALS;

  const isFinalStep = () => {
    if (persona === 'seller' && step === 4) return true;
    if (persona === 'buyer' && step === 5) return true;
    if (persona === 'both' && step === 5) return true;
    return false;
  };

  // ── Navigation ───────────────────────────────────────────────────

  const handleContinue = () => {
    if (step === 1 && persona) {
      setStep(2);
    } else if (step === 2 && goal) {
      setStep(3);
    } else if (step === 3) {
      if (persona === 'buyer') {
        setStep(5);
      } else {
        setStep(4);
      }
    } else if (step === 4 && persona === 'both') {
      setStep(5);
    }
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else if (step === 4) setStep(3);
    else if (step === 5) {
      if (persona === 'both') setStep(4);
      else setStep(3);
    }
  };

  const handleSkip = () => {
    if (step === 3) {
      if (persona === 'buyer') {
        setStep(5);
      } else {
        setStep(4);
      }
    } else if (step === 4) {
      if (persona === 'both') {
        setStep(5);
      } else {
        handleSubmit();
      }
    } else if (step === 5) {
      handleSubmit();
    }
  };

  const toggleBuyerIndustry = (industry) => {
    if (buyerIndustries.includes(industry)) {
      setBuyerIndustries(buyerIndustries.filter((i) => i !== industry));
    } else {
      setBuyerIndustries([...buyerIndustries, industry]);
    }
  };

  // ── Submit ───────────────────────────────────────────────────────

  const [saveError, setSaveError] = useState(null);

  const handleSubmit = async () => {
    if (!persona || !goal) {
      // persona or goal not set — cannot submit
      return;
    }
    setSubmitting(true);
    setSaveError(null);

    const resolvedRole = roleTitle === 'Other' && otherRole ? otherRole : roleTitle;

    // time_horizon is derived from the goal label now (the picker was
    // removed — see GOAL_TO_TIME_HORIZON). Returning users with an
    // existing time_horizon keep that value if their goal doesn't map
    // to a new bucket; new users get the derived one.
    const derivedHorizon = GOAL_TO_TIME_HORIZON[goal];
    const variables = {
      persona,
      goal,
      timeHorizon: derivedHorizon || timeHorizon || null,
      roleTitle: resolvedRole || null,
      companyName: companyName || null,
      geography: geography || null,
      ownerRole: ownerRole || null,
      industrySector: industrySector || null,
      industryDescription: industryDescription || null,
      businessSizeBand: businessSizeBand || null,
      saleObjective: saleObjective || null,
      buyerType: buyerType || null,
      preferredIndustries: buyerIndustries.length > 0 ? buyerIndustries : null,
      targetGeography: targetGeography || null,
      targetSizeRange: targetSize || null,
      dealRole: dealRole || null,
    };

    try {
      await onSave(variables);
      if (window.ahoy) {
        window.ahoy.track('succeedo_onboarding_completed', { persona, goal });
      }
      window.location.href = '/succeedo/home';
    } catch (err) {
      console.error('Error saving onboarding:', err);
      setSaveError(err.message || 'Failed to save. Please try again.');
      setSubmitting(false);
    }
  };

  const isLoading = submitting || savingExternal;

  // If user already completed onboarding and no ?step= requested, show nothing
  // (the useEffect redirect to /succeedo/home handles it).
  // When ?step=N IS present, we need to render the wizard at that step.
  if (currentUser && currentUser.succeedo_persona && currentUser.succeedo_goal && !(requestedStep && requestedStep >= 3)) {
    return null;
  }

  // ── Render ───────────────────────────────────────────────────────

  return (
    <div className="dark min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-4">
      <div className="bg-white rounded-[16px] p-8 max-w-[600px] w-full shadow-2xl relative">
        {/* Close button */}
        <button
          onClick={() => { window.location.href = '/succeedo/home'; }}
          className="absolute top-6 right-6 text-[#a3a3a3] hover:text-[#0a0a0a] transition-colors"
        >
          <X className="w-5 h-5" strokeWidth={2} />
        </button>

        {/* Progress Bar */}
        <ProgressBar currentStep={displayStep()} totalSteps={totalSteps} />

        {/* ── Step 1: Persona ──────────────────────────────────────── */}
        {step === 1 && (
          <div>
            <h1 className="font-serif text-[40px] font-normal text-[#0a0a0a] tracking-[-0.02em] mb-3 leading-[1.1]">
              Welcome to Succeedo &mdash; let&apos;s personalize your experience
            </h1>
            <p className="text-[16px] text-[#737373] mb-8">What best describes you?</p>

            <div className="space-y-3 mb-8">
              {PERSONA_OPTIONS.map((option) => (
                <SelectionCard
                  key={option.value}
                  icon={option.icon}
                  title={option.title}
                  value={option.value}
                  selected={persona === option.value}
                  onClick={() => setPersona(option.value)}
                />
              ))}
            </div>

            <button
              onClick={handleContinue}
              disabled={!persona}
              className="w-full bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all disabled:bg-[#a3a3a3] disabled:cursor-not-allowed hover:bg-[#262626]"
            >
              Continue &rarr;
            </button>
          </div>
        )}

        {/* ── Step 2: Goal (time horizon is now baked into the goal labels) ──── */}
        {step === 2 && (
          <div>
            <h1 className="font-serif text-[40px] font-normal text-[#0a0a0a] tracking-[-0.02em] mb-3 leading-[1.1]">
              What&apos;s your primary focus right now?
            </h1>
            <p className="text-[16px] text-[#737373] mb-8">
              This helps us prioritize the right tools and guidance for your journey
            </p>

            <div className="space-y-3 mb-8">
              {currentGoals.map((goalOption) => (
                <SelectionCard
                  key={goalOption}
                  title={goalOption}
                  value={goalOption}
                  selected={goal === goalOption}
                  onClick={() => setGoal(goalOption)}
                />
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors"
              >
                &larr; Back
              </button>
              <button
                onClick={handleContinue}
                disabled={!goal}
                className={`bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all ${
                  !goal ? 'bg-[#a3a3a3] cursor-not-allowed' : 'hover:bg-[#262626]'
                }`}
              >
                Continue &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: About You (skippable) ────────────────────────── */}
        {step === 3 && (
          <div>
            <h1 className="font-serif text-[32px] font-normal text-[#0a0a0a] tracking-[-0.02em] mb-2 leading-[1.1]">
              A bit about you
            </h1>
            <p className="text-[16px] text-[#737373] mb-8">
              This helps us tailor your experience
            </p>

            <div className="space-y-4 mb-8">

              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Company / firm name</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Optional"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[13px] text-[#737373] mb-2">Geography</label>
                <input
                  type="text"
                  value={geography}
                  onChange={(e) => setGeography(e.target.value)}
                  placeholder="Postal or ZIP code, province or state"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors"
              >
                Skip for now &rarr;
              </button>
              <button
                onClick={handleContinue}
                className="bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all hover:bg-[#262626]"
              >
                Continue &rarr;
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Seller Context (skippable) ───────────────────── */}
        {step === 4 && (
          <div>
            <h1 className="font-serif text-[32px] font-normal text-[#0a0a0a] tracking-[-0.02em] mb-8 leading-[1.1]">
              Tell us about your business
            </h1>

            <div className="space-y-6 mb-8">
              {/* Owner role */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  What&apos;s your role?
                </label>
                <div className="space-y-3">
                  {SELLER_ROLE_OPTIONS.map((option) => (
                    <SelectionCard
                      key={option.value}
                      title={option.title}
                      value={option.value}
                      selected={ownerRole === option.value}
                      onClick={() => setOwnerRole(option.value)}
                    />
                  ))}
                </div>
              </div>

              {/* Industry (single-select) */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  What industry?
                </label>
                <div className="flex gap-2 flex-wrap">
                  {INDUSTRIES.map((industry) => (
                    <IndustryPill
                      key={industry}
                      label={industry}
                      selected={industrySector === industry}
                      onClick={() => setIndustrySector(industry)}
                    />
                  ))}
                </div>
              </div>

              {/* Business description */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  Describe your business in a sentence
                </label>
                <textarea
                  value={industryDescription}
                  onChange={(e) => setIndustryDescription(e.target.value)}
                  rows={2}
                  placeholder="e.g., HVAC contracting company serving residential clients"
                  className="w-full bg-white border border-[#e5e5e5] rounded-[8px] px-4 py-3 text-[#0a0a0a] focus:border-[hsl(var(--succeedo-orange))] focus:outline-none resize-none"
                />
              </div>

              {/* Business size */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  Annual revenue range
                </label>
                <div className="flex gap-2 flex-wrap">
                  {SIZE_BANDS.map((size) => (
                    <PillButton
                      key={size}
                      label={size}
                      selected={businessSizeBand === size}
                      onClick={() => setBusinessSizeBand(size)}
                    />
                  ))}
                </div>
              </div>

              {/* Sale objective */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  What are you looking to do?
                </label>
                <div className="space-y-3">
                  {EXIT_TYPES.map((option) => (
                    <SelectionCard
                      key={option.value}
                      title={option.title}
                      value={option.value}
                      selected={saleObjective === option.value}
                      onClick={() => setSaleObjective(option.value)}
                    />
                  ))}
                </div>
              </div>
            </div>

            {saveError && (
              <div className="bg-red-50 border border-red-200 rounded-[8px] p-3 mb-4">
                <p className="text-red-600 text-[13px]">{saveError}</p>
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors"
              >
                Skip for now &rarr;
              </button>
              {persona === 'both' ? (
                <button
                  onClick={handleContinue}
                  className="bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all hover:bg-[#262626]"
                >
                  Continue to buyer criteria &rarr;
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className={`bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all ${
                    isLoading ? 'bg-[#a3a3a3] cursor-not-allowed' : 'hover:bg-[#262626]'
                  }`}
                >
                  {isLoading ? 'Saving...' : 'Get Started \u2192'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Step 5: Buyer Criteria (skippable) ───────────────────── */}
        {step === 5 && (
          <div>
            <h1 className="font-serif text-[32px] font-normal text-[#0a0a0a] tracking-[-0.02em] mb-8 leading-[1.1]">
              What are you looking for?
            </h1>

            <div className="space-y-6 mb-8">
              {/* Buyer type */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  I am a...
                </label>
                <div className="space-y-3">
                  {BUYER_TYPE_OPTIONS.map((option) => (
                    <SelectionCard
                      key={option.value}
                      title={option.title}
                      value={option.value}
                      selected={buyerType === option.value}
                      onClick={() => setBuyerType(option.value)}
                    />
                  ))}
                </div>
              </div>

              {/* Industries (multi-select) */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  Industries I&apos;m interested in
                </label>
                <div className="flex gap-2 flex-wrap">
                  {INDUSTRIES.map((industry) => (
                    <IndustryPill
                      key={industry}
                      label={industry}
                      selected={buyerIndustries.includes(industry)}
                      onClick={() => toggleBuyerIndustry(industry)}
                    />
                  ))}
                </div>
              </div>

              {/* Target geography */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  Target geography
                </label>
                <div className="flex gap-2 flex-wrap">
                  {GEO_OPTIONS.map((geo) => (
                    <PillButton
                      key={geo}
                      label={geo}
                      selected={targetGeography === geo}
                      onClick={() => setTargetGeography(geo)}
                    />
                  ))}
                </div>
              </div>

              {/* Target business size */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  Target business size
                </label>
                <div className="flex gap-2 flex-wrap">
                  {SIZE_BANDS.map((size) => (
                    <PillButton
                      key={size}
                      label={size}
                      selected={targetSize === size}
                      onClick={() => setTargetSize(size)}
                    />
                  ))}
                </div>
              </div>

              {/* Deal role */}
              <div>
                <label className="block font-mono text-[11px] uppercase text-[#a3a3a3] tracking-wide mb-3">
                  My role in this deal
                </label>
                <div className="flex gap-2 flex-wrap">
                  {DEAL_ROLE_OPTIONS.map((role) => (
                    <PillButton
                      key={role}
                      label={role}
                      selected={dealRole === role}
                      onClick={() => setDealRole(role)}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="text-[14px] text-[#737373] hover:text-[#0a0a0a] transition-colors"
              >
                Skip for now &rarr;
              </button>
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`bg-[#0a0a0a] text-white rounded-full px-6 py-3 font-medium transition-all ${
                  isLoading ? 'bg-[#a3a3a3] cursor-not-allowed' : 'hover:bg-[#262626]'
                }`}
              >
                {isLoading ? 'Saving...' : 'Get Started \u2192'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonaWizard;
